import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  canAccess,
  canWrite,
  fhir,
  normalizeAbhaAddress,
  normalizeAbhaNumber,
  normalizeMobile,
  type HeldRecord,
  type HiType,
  type PracticePatientLink,
  type PublicConsent,
} from '@curentia/shared';
import { ErrorText, Field, GroupedCode, LockButton, Page, Submit, formatMobile } from '../components';
import { messageFor } from '../lib/errors';
import { ACCESS_DAYS, ASKABLE_TYPES, CONSENT_STATUS_LABELS, HI_TYPE_LABELS, formatDate, readingsOf, sourcesOf, timelineOf } from '../lib/health';
import { PracticeError, type PracticeRecords } from '../lib/records';
import { useLoad } from '../lib/useLoad';
import { ScanButton } from '../qr';
import { useAction, useDevice, useSnapshot } from '../state';
import { ReadingsPanel, Timeline } from './Health';
import { PrescriptionForm } from './Prescriptions';

const groups = (id: string) => (id.match(/.{1,4}/g) ?? []).join(' ');
const DAY_MS = 24 * 60 * 60 * 1000;

interface PatientView extends fhir.PatientDetails {
  id: string;
}

const GENDERS: Record<string, string> = { female: 'Female', male: 'Male', other: 'Other', unknown: 'Not stated' };

function patientView(record: HeldRecord): PatientView {
  return { id: record.id, ...fhir.patientDetails(record.resource), ...(record.accountId ? { accountId: record.accountId } : {}) };
}

const byName = (a: PatientView, b: PatientView) => a.name.localeCompare(b.name);
const resourceOf = (patient: fhir.Patient) => patient as unknown as Record<string, unknown>;

/** Opens the practice's records for this device, reopening them when the practice changes. */
function usePracticeRecords(practiceId: string) {
  const device = useDevice();
  const { practicesVersion } = useSnapshot();
  const [records, setRecords] = useState<PracticeRecords | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let current = true;
    device.records(practiceId).then(
      (opened) => current && setRecords(opened),
      (err: unknown) => current && setError(messageFor(err)),
    );
    return () => {
      current = false;
    };
  }, [device, practiceId, practicesVersion]);
  return { records, error };
}

/** Shown when the server names a different hub than this device first used, until the person has checked it. */
function HubChanged({ practiceId, onTrusted }: { practiceId: string; onTrusted: () => void }) {
  const device = useDevice();
  const [change, setChange] = useState<{ pinned: string; current: string } | null>(null);
  const { busy, error, run } = useAction();
  useEffect(() => {
    void device.holderChange(practiceId).then(setChange, () => undefined);
  }, [device, practiceId]);
  if (!change) return null;
  return (
    <section className="card warning" aria-label="The hub has changed">
      <h2>The clinic’s hub has changed</h2>
      <p>Before this device sends anything to it, check with your practice admin that the new hub’s fingerprint matches the one shown on the hub PC.</p>
      <p className="muted">New hub</p>
      <GroupedCode code={groups(change.current)} separator=" " label="New hub fingerprint" testId="new-hub-fingerprint" />
      <p className="muted">Hub this device used before</p>
      <GroupedCode code={groups(change.pinned)} separator=" " label="Previous hub fingerprint" testId="old-hub-fingerprint" />
      <ErrorText error={error} />
      <button
        type="button"
        className="button"
        disabled={busy}
        onClick={() =>
          void run(async () => {
            await device.trustHolder(practiceId);
            onTrusted();
          })
        }
      >
        The fingerprint matches. Use the new hub
      </button>
    </section>
  );
}

/** A Patient resource from the practice's details, keeping its record number and the linked account. */
function buildPatientFrom(details: fhir.PatientDetails, id: string, practiceName: string): fhir.Patient {
  return fhir.buildPatient({
    name: details.name,
    ...(details.gender ? { gender: details.gender } : {}),
    ...(details.birthDate ? { birthDate: details.birthDate } : {}),
    ...(details.mobile ? { mobile: details.mobile } : {}),
    ...(details.abhaNumber ? { abhaNumber: details.abhaNumber } : {}),
    ...(details.abhaAddress ? { abhaAddress: details.abhaAddress } : {}),
    ...(details.accountId ? { accountId: details.accountId } : {}),
    medicalRecord: details.medicalRecord ?? { value: id, assigner: practiceName },
  });
}

export function PatientsScreen() {
  const { id = '' } = useParams();
  const { records, error: openError } = usePracticeRecords(id);
  const [patients, setPatients] = useState<PatientView[] | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [changed, setChanged] = useState(false);
  const [filter, setFilter] = useState('');
  // A link the code card is adding right now, kept out of the list of links waiting to be added.
  const [adding, setAdding] = useState<string | null>(null);

  const sync = useCallback(async (opened: PracticeRecords) => {
    setPatients((await opened.cachedPatients()).map(patientView).sort(byName));
    try {
      setPatients((await opened.syncPatients()).map(patientView).sort(byName));
      setStatus(null);
      setChanged(false);
    } catch (err) {
      setChanged(err instanceof PracticeError && err.code === 'holder_changed');
      setStatus(opened.local ? messageFor(err) : `Showing what this device already has. ${messageFor(err)}`);
    }
  }, []);

  useEffect(() => {
    if (records) void sync(records);
  }, [records, sync]);

  if (openError) {
    return (
      <Page actions={<LockButton />}>
        <h1>Patients</h1>
        <ErrorText error={openError} />
        <div className="links">
          <Link to={`/practices/${id}`}>Back</Link>
        </div>
      </Page>
    );
  }
  const query = filter.trim().toLowerCase();
  const digits = query.replace(/\D/g, '');
  const shown = (patients ?? []).filter((p) => p.name.toLowerCase().includes(query) || (digits.length >= 4 && (p.mobile ?? '').includes(digits)));
  const registers = records ? canWrite(records.roles, 'demographics') : false;
  return (
    <Page actions={<LockButton />}>
      <h1>Patients</h1>
      {records ? <p className="muted">{records.practice.name}</p> : null}
      {changed && records ? <HubChanged practiceId={records.practice.id} onTrusted={() => void sync(records)} /> : null}
      {status ? (
        <p className="note" role="status">
          {status}
        </p>
      ) : null}
      {records && registers ? (
        <div className="stack spaced-below">
          <AddPatient records={records} onAdded={() => void sync(records)} />
          <AddWithCode records={records} patients={patients ?? []} onAdded={() => void sync(records)} onLinked={setAdding} />
        </div>
      ) : null}
      {records && registers && patients ? <WaitingLinks records={records} patients={patients} adding={adding} onAdded={() => void sync(records)} /> : null}
      <section className="card" aria-labelledby="patients-heading">
        <h2 id="patients-heading">The practice’s patients</h2>
        <Field label="Find a patient" hint="By name, or by four or more digits of their mobile number" value={filter} onChange={(e) => setFilter(e.target.value)} />
        {patients === null ? <p className="muted">Loading…</p> : null}
        {patients && shown.length === 0 ? <p className="muted">{patients.length === 0 ? 'No patients yet.' : 'Nobody matches.'}</p> : null}
        {shown.length > 0 ? (
          <ul className="list" aria-label="Patients">
            {shown.map((patient) => (
              <li key={patient.id}>
                <Link to={`/practices/${id}/patients/${patient.id}`} className="list-link">
                  <strong>{patient.name}</strong>
                  {patient.gender || patient.birthDate || patient.accountId ? (
                    <span className="muted">
                      {[patient.gender ? GENDERS[patient.gender] : null, patient.birthDate ? `born ${patient.birthDate}` : null, patient.accountId ? 'on Curentia' : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
      <div className="links">
        <Link to={`/practices/${id}`}>Back</Link>
      </div>
    </Page>
  );
}

interface DetailsForm {
  name: string;
  gender: string;
  birthDate: string;
  mobile: string;
  abhaNumber: string;
  abhaAddress: string;
}

const EMPTY_FORM: DetailsForm = { name: '', gender: '', birthDate: '', mobile: '', abhaNumber: '', abhaAddress: '' };

function formOf(details: fhir.PatientDetails): DetailsForm {
  return {
    name: details.name,
    gender: details.gender ?? '',
    birthDate: details.birthDate ?? '',
    mobile: details.mobile ?? '',
    abhaNumber: details.abhaNumber ?? '',
    abhaAddress: details.abhaAddress ?? '',
  };
}

/** Checks the form and returns the details to save, or a message. */
function detailsFrom(form: DetailsForm): fhir.PatientDetails | string {
  const name = form.name.trim();
  if (!name) return 'Enter the patient’s name.';
  if (name.length > 100) return 'Use 100 characters or fewer for the name.';
  const mobile = form.mobile.trim() ? normalizeMobile(form.mobile) : undefined;
  if (mobile === null) return 'Enter a valid Indian mobile number, or leave it empty.';
  const abhaNumber = form.abhaNumber.trim() ? normalizeAbhaNumber(form.abhaNumber) : undefined;
  if (abhaNumber === null) return 'Enter the 14-digit ABHA number, or leave it empty.';
  const abhaAddress = form.abhaAddress.trim() ? normalizeAbhaAddress(form.abhaAddress) : undefined;
  if (abhaAddress === null) return 'Enter an ABHA address such as name@abdm, or leave it empty.';
  if (form.birthDate && (Number.isNaN(Date.parse(form.birthDate)) || Date.parse(form.birthDate) > Date.now())) return 'Enter a date of birth in the past.';
  return {
    name,
    ...(form.gender ? { gender: form.gender as NonNullable<fhir.PatientDetails['gender']> } : {}),
    ...(form.birthDate ? { birthDate: form.birthDate } : {}),
    ...(mobile ? { mobile } : {}),
    ...(abhaNumber ? { abhaNumber } : {}),
    ...(abhaAddress ? { abhaAddress } : {}),
  };
}

function DetailsFields({ form, setForm }: { form: DetailsForm; setForm: (form: DetailsForm) => void }) {
  const genderId = useId();
  const set = (key: keyof DetailsForm) => (event: { target: { value: string } }) => setForm({ ...form, [key]: event.target.value });
  return (
    <>
      <Field label="Patient’s full name" value={form.name} onChange={set('name')} autoComplete="off" required />
      <div className="field">
        <label htmlFor={genderId}>Sex</label>
        <select id={genderId} value={form.gender} onChange={set('gender')}>
          <option value="">Not stated</option>
          <option value="female">Female</option>
          <option value="male">Male</option>
          <option value="other">Other</option>
        </select>
      </div>
      <Field label="Date of birth (optional)" type="date" value={form.birthDate} max={new Date().toISOString().slice(0, 10)} onChange={set('birthDate')} />
      <Field label="Patient’s mobile number (optional)" type="tel" inputMode="tel" autoComplete="off" value={form.mobile} onChange={set('mobile')} />
      <Field label="ABHA number (optional)" inputMode="numeric" autoComplete="off" hint="14 digits" value={form.abhaNumber} onChange={set('abhaNumber')} />
      <Field label="ABHA address (optional)" autoComplete="off" hint="Such as name@abdm" value={form.abhaAddress} onChange={set('abhaAddress')} />
    </>
  );
}

function AddPatient({ records, onAdded }: { records: PracticeRecords; onAdded: () => void }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [open, setOpen] = useState(false);
  const { busy, error, setError, run } = useAction();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const details = detailsFrom(form);
    if (typeof details === 'string') return setError(details);
    void run(async () => {
      const id = globalThis.crypto.randomUUID();
      await records.save({ id, patientId: id, kind: 'patient', resource: resourceOf(buildPatientFrom(details, id, records.practice.name)), baseVersion: 0 });
      setForm(EMPTY_FORM);
      setOpen(false);
      onAdded();
    });
  };

  if (!open) {
    return (
      <button type="button" className="button" onClick={() => setOpen(true)}>
        Add a patient
      </button>
    );
  }
  return (
    <section className="card" aria-labelledby="add-patient-heading">
      <h2 id="add-patient-heading">Add a patient</h2>
      <form onSubmit={submit} noValidate>
        <DetailsFields form={form} setForm={setForm} />
        <ErrorText error={error} />
        <Submit busy={busy} busyText="Saving…">
          Save the patient
        </Submit>
      </form>
      <button type="button" className="button secondary small spaced" onClick={() => setOpen(false)}>
        Cancel
      </button>
    </section>
  );
}

/** Practice patients not yet linked who may be this account's person, by mobile number or ABHA. */
function likelyMatches(link: PracticePatientLink, patients: PatientView[]): PatientView[] {
  return patients.filter(
    (p) =>
      !p.accountId &&
      ((p.mobile !== undefined && p.mobile === link.patient.mobile) ||
        (p.abhaNumber !== undefined && p.abhaNumber === link.patient.abhaNumber) ||
        (p.abhaAddress !== undefined && p.abhaAddress === link.patient.abhaAddress)),
  );
}

/**
 * A linked account the practice's records do not name yet: add it as a new
 * patient, filled in from the account, or put it on the record of someone
 * already registered.
 */
function AttachLink({ link, records, patients, onDone }: { link: PracticePatientLink; records: PracticeRecords; patients: PatientView[]; onDone: (patientId: string) => void }) {
  const { busy, error, run } = useAction();
  const matches = likelyMatches(link, patients);
  const addNew = () =>
    void run(async () => {
      const id = globalThis.crypto.randomUUID();
      const details: fhir.PatientDetails = {
        name: link.patient.name,
        mobile: link.patient.mobile,
        accountId: link.patient.accountId,
        ...(link.patient.abhaNumber ? { abhaNumber: link.patient.abhaNumber } : {}),
        ...(link.patient.abhaAddress ? { abhaAddress: link.patient.abhaAddress } : {}),
      };
      await records.save({ id, patientId: id, kind: 'patient', resource: resourceOf(buildPatientFrom(details, id, records.practice.name)), baseVersion: 0 });
      onDone(id);
    });
  const attach = (patient: PatientView) =>
    void run(async () => {
      const current = (await records.fetchRecords(patient.id)).find((r) => r.kind === 'patient');
      if (!current) throw new PracticeError('not_found', 'That patient’s record could not be opened.');
      const linked = fhir.withAccountId(current.resource as unknown as fhir.Patient, link.patient.accountId);
      await records.save({ id: patient.id, patientId: patient.id, kind: 'patient', resource: resourceOf(linked), baseVersion: current.version });
      onDone(patient.id);
    });
  return (
    <li className="member" aria-label={link.patient.name}>
      <strong>{link.patient.name}</strong>
      <span className="muted">
        {formatMobile(link.patient.mobile)}
        {link.patient.abhaAddress ? ` · ${link.patient.abhaAddress}` : ''}
      </span>
      <ErrorText error={error} />
      <div className="actions">
        {matches.map((patient) => (
          <button key={patient.id} type="button" className="button small" disabled={busy} onClick={() => attach(patient)}>
            Same person as {patient.name}
            {patient.birthDate ? `, born ${patient.birthDate}` : ''}
          </button>
        ))}
        <button type="button" className={`button small${matches.length > 0 ? ' secondary' : ''}`} disabled={busy} onClick={addNew}>
          Add as a new patient
        </button>
      </div>
    </li>
  );
}

/** Links the patient whose code the front desk scans or types, then adds them to the practice's patients. */
function AddWithCode({
  records,
  patients,
  onAdded,
  onLinked,
}: {
  records: PracticeRecords;
  patients: PatientView[];
  onAdded: () => void;
  onLinked: (linkId: string | null) => void;
}) {
  const device = useDevice();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [link, setLink] = useState<PracticePatientLink | null>(null);
  const { busy, error, run } = useAction();
  const linkWith = (text: string) =>
    void run(async () => {
      const made = await device.linkPatient(records.practice.id, text);
      onLinked(made.id);
      setLink(made);
      setCode('');
    });

  if (!open) {
    return (
      <button type="button" className="button secondary" onClick={() => setOpen(true)}>
        Add with the patient’s code
      </button>
    );
  }
  const already = link ? patients.find((p) => p.accountId === link.patient.accountId) : undefined;
  return (
    <section className="card" aria-labelledby="code-heading">
      <h2 id="code-heading">Add with the patient’s code</h2>
      {!link ? (
        <>
          <p>Ask the patient to open Curentia and choose Show my code to a clinic. Scan it, or type the ten characters under it.</p>
          <ScanButton onScan={linkWith} />
          <form
            onSubmit={(event) => {
              event.preventDefault();
              linkWith(code);
            }}
            noValidate
          >
            <Field label="Patient’s code" autoComplete="off" autoCapitalize="characters" spellCheck={false} value={code} onChange={(e) => setCode(e.target.value)} />
            <ErrorText error={error} />
            <Submit busy={busy} busyText="Linking…">
              Link the patient
            </Submit>
          </form>
        </>
      ) : already ? (
        <>
          <p role="status">Linked. {already.name} is already one of the practice’s patients.</p>
          <Link className="button small" to={`/practices/${records.practice.id}/patients/${already.id}`}>
            Open {already.name}
          </Link>
        </>
      ) : (
        <>
          <p role="status">Linked to {link.patient.name}’s Curentia account. Add them to the practice’s patients:</p>
          <ul className="list">
            <AttachLink
              link={link}
              records={records}
              patients={patients}
              onDone={(patientId) => {
                onAdded();
                void navigate(`/practices/${records.practice.id}/patients/${patientId}`);
              }}
            />
          </ul>
        </>
      )}
      <button
        type="button"
        className="button secondary small spaced"
        onClick={() => {
          setOpen(false);
          setLink(null);
          onLinked(null);
        }}
      >
        Close
      </button>
    </section>
  );
}

/** Accounts linked to the practice, by code or by a request the patient approved, that no patient record names yet. */
function WaitingLinks({ records, patients, adding, onAdded }: { records: PracticeRecords; patients: PatientView[]; adding: string | null; onAdded: () => void }) {
  const device = useDevice();
  const { linksVersion } = useSnapshot();
  const { data } = useLoad(() => device.api.practiceLinks(records.practice.id), [device, records.practice.id, linksVersion]);
  const known = new Set(patients.flatMap((p) => (p.accountId ? [p.accountId] : [])));
  const waiting = (data?.links ?? []).filter((link) => !known.has(link.patient.accountId) && link.id !== adding);
  if (waiting.length === 0) return null;
  return (
    <section className="card tinted" aria-labelledby="waiting-links-heading">
      <h2 id="waiting-links-heading">Linked accounts to add</h2>
      <p className="muted">These patients linked their Curentia accounts to the practice. Add each to the practice’s patients, or put the link on the record of someone already registered.</p>
      <ul className="list">
        {waiting.map((link) => (
          <AttachLink key={link.id} link={link} records={records} patients={patients} onDone={onAdded} />
        ))}
      </ul>
    </section>
  );
}

// ---- One patient --------------------------------------------------------------------

export function PatientScreen() {
  const { id = '', patientId = '' } = useParams();
  const device = useDevice();
  const { account, consentsVersion, recordsVersion } = useSnapshot();
  const { records, error: openError } = usePracticeRecords(id);
  const [held, setHeld] = useState<HeldRecord[] | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const load = useCallback(
    async (opened: PracticeRecords) => {
      const cached = await opened.cachedRecords(patientId);
      if (cached.length > 0) setHeld(cached);
      try {
        setHeld(await opened.fetchRecords(patientId));
        setStatus(null);
      } catch (err) {
        setStatus(cached.length > 0 ? `Showing what this device already has. ${messageFor(err)}` : messageFor(err));
      }
    },
    [patientId],
  );

  // Shared records arrive after the patient approves: look again when that happens.
  useEffect(() => {
    if (records) void load(records);
  }, [records, load, consentsVersion, recordsVersion]);

  const demographics = held?.find((r) => r.kind === 'patient');
  const patient = demographics ? patientView(demographics) : null;
  const clinical = records ? canAccess(records.roles, 'clinical') : false;
  const doctor = records?.roles.includes('doctor') ?? false;
  const accountId = patient?.accountId;
  const consents = useLoad(
    async () => (doctor && accountId ? (await device.api.consents({ practiceId: id, patientId: accountId })).consents : []),
    [device, id, doctor, accountId, consentsVersion],
  );

  // While an approval is fresh, a hub may still be receiving its records, so look again now and then.
  const fresh = (consents.data ?? []).some((c) => c.status === 'approved' && c.decidedAt !== null && Date.now() - Date.parse(c.decidedAt) < 2 * 60_000);
  useEffect(() => {
    if (!fresh || !records) return;
    const timer = setInterval(() => void load(records), 5_000);
    return () => clearInterval(timer);
  }, [fresh, records, load]);

  const [sentNote, setSentNote] = useState<string | null>(null);
  const addReading = async (reading: fhir.Reading) => {
    if (!records || !patient) return;
    setSentNote(null);
    const bundle = fhir.buildWellnessRecord({
      date: reading.at,
      patient: {
        name: patient.name,
        medicalRecord: patient.medicalRecord ?? { value: patient.id, assigner: records.practice.name },
        ...(patient.accountId ? { accountId: patient.accountId } : {}),
      },
      readings: [reading],
      recordedBy: { name: account?.name ?? 'Doctor', ...(account?.registration ? { registration: account.registration } : {}) },
    });
    const saved = await records.save({ id: globalThis.crypto.randomUUID(), patientId: patient.id, kind: 'document', hiType: 'WellnessRecord', resource: bundle as unknown as Record<string, unknown>, baseVersion: 0 });
    await load(records);
    // A linked patient gets a copy on their phone, which is where they see their readings.
    if (patient.accountId) {
      try {
        await device.sendToPatient(saved, { id: records.practice.id, name: records.practice.name }, patient.accountId);
        setSentNote(`Saved and sent to ${patient.name}’s phone.`);
      } catch {
        setSentNote(`Saved. It could not be sent to ${patient.name}’s phone just now.`);
      }
    }
  };

  return (
    <Page actions={<LockButton />}>
      <ErrorText error={openError} />
      {status ? (
        <p className="note" role="status">
          {status}
        </p>
      ) : null}
      {patient && demographics && records ? (
        <>
          <h1>{patient.name}</h1>
          <DetailsCard record={demographics} patient={patient} records={records} onSaved={() => void load(records)} />
          <AccountCard record={demographics} patient={patient} records={records} onSaved={() => void load(records)} />
          {canWrite(records.roles, 'prescriptions') ? (
            <PrescriptionsCard records={records} patient={patient} onSaved={() => void load(records)} />
          ) : null}
          {doctor && patient.accountId ? <HistoryCard practiceId={id} patient={patient} consents={consents.data} error={consents.error} onAsked={consents.reload} /> : null}
        </>
      ) : held === null && !status && !openError ? (
        <p className="muted">Loading…</p>
      ) : null}
      {patient && clinical && records ? (
        <>
          <section className="card" aria-labelledby="timeline-heading">
            <h2 id="timeline-heading">Timeline</h2>
            <Timeline
              items={timelineOf(held ?? [], records.practice.name)}
              empty="No records yet."
              linkFor={(item) => (item.hiType === 'Prescription' ? `/practices/${id}/prescriptions/${item.id}` : null)}
            />
          </section>
          {sentNote ? (
            <p className="note" role="status">
              {sentNote}
            </p>
          ) : null}
          {canWrite(records.roles, 'clinical') ? (
            <ReadingsPanel points={readingsOf(held ?? [], records.practice.name)} here={records.practice.name} onAdd={addReading} />
          ) : (
            <ReadingsPanel points={readingsOf(held ?? [], records.practice.name)} here={records.practice.name} />
          )}
        </>
      ) : null}
      <div className="links">
        <Link to={`/practices/${id}/patients`}>Back</Link>
      </div>
    </Page>
  );
}

/** A doctor's prescriptions card: the newest prescriptions are on the timeline, and the form writes a new one. */
function PrescriptionsCard({ records, patient, onSaved }: { records: PracticeRecords; patient: PatientView; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState<{ id: string; outcome: string } | null>(null);
  return (
    <section className="card" aria-labelledby="prescription-heading">
      <h2 id="prescription-heading">Prescriptions</h2>
      {done ? (
        <div className="stack spaced-below">
          <p className="note" role="status">
            {done.outcome}
          </p>
          <Link className="button secondary small" to={`/practices/${records.practice.id}/prescriptions/${done.id}`}>
            Open and print
          </Link>
        </div>
      ) : null}
      {open ? (
        <>
          <PrescriptionForm
            records={records}
            patient={{ id: patient.id, details: patient }}
            onSaved={(record, outcome) => {
              setDone({ id: record.id, outcome });
              setOpen(false);
              onSaved();
            }}
          />
          <button type="button" className="button secondary small spaced" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </>
      ) : (
        <button
          type="button"
          className="button"
          onClick={() => {
            setDone(null);
            setOpen(true);
          }}
        >
          Write a prescription
        </button>
      )}
    </section>
  );
}

function DetailsCard({ record, patient, records, onSaved }: { record: HeldRecord; patient: PatientView; records: PracticeRecords; onSaved: () => void }) {
  const [form, setForm] = useState<DetailsForm | null>(null);
  const { busy, error, setError, run } = useAction();
  const edits = canWrite(records.roles, 'demographics');
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!form) return;
    const details = detailsFrom(form);
    if (typeof details === 'string') return setError(details);
    void run(async () => {
      const kept = { ...(patient.accountId ? { accountId: patient.accountId } : {}), ...(patient.medicalRecord ? { medicalRecord: patient.medicalRecord } : {}) };
      const resource = buildPatientFrom({ ...details, ...kept }, patient.id, records.practice.name);
      await records.save({ id: patient.id, patientId: patient.id, kind: 'patient', resource: resourceOf(resource), baseVersion: record.version });
      setForm(null);
      onSaved();
    });
  };
  if (form) {
    return (
      <section className="card" aria-labelledby="edit-heading">
        <h2 id="edit-heading">Edit details</h2>
        <form onSubmit={submit} noValidate>
          <DetailsFields form={form} setForm={setForm} />
          <ErrorText error={error} />
          <Submit busy={busy} busyText="Saving…">
            Save the details
          </Submit>
        </form>
        <button type="button" className="button secondary small spaced" onClick={() => setForm(null)}>
          Cancel
        </button>
      </section>
    );
  }
  return (
    <section className="card" aria-label="Patient details">
      <dl>
        <dt>Sex</dt>
        <dd>{patient.gender ? GENDERS[patient.gender] : 'Not stated'}</dd>
        <dt>Born</dt>
        <dd>{patient.birthDate ?? 'Not given'}</dd>
        <dt>Mobile</dt>
        <dd>{patient.mobile ? formatMobile(patient.mobile) : 'Not given'}</dd>
        {patient.abhaNumber ? (
          <>
            <dt>ABHA</dt>
            <dd>{patient.abhaNumber}</dd>
          </>
        ) : null}
        {patient.abhaAddress ? (
          <>
            <dt>ABHA address</dt>
            <dd>{patient.abhaAddress}</dd>
          </>
        ) : null}
      </dl>
      {edits ? (
        <button type="button" className="button secondary small spaced" onClick={() => setForm(formOf(patient))}>
          Edit details
        </button>
      ) : null}
    </section>
  );
}

/** Whether the record is linked to the patient's Curentia account, and the ways to link it. */
function AccountCard({ record, patient, records, onSaved }: { record: HeldRecord; patient: PatientView; records: PracticeRecords; onSaved: () => void }) {
  const device = useDevice();
  const [code, setCode] = useState('');
  const [identifier, setIdentifier] = useState(patient.mobile ? formatMobile(patient.mobile) : (patient.abhaAddress ?? ''));
  const [sent, setSent] = useState(false);
  const linking = useAction();
  const asking = useAction();
  const linkWith = (text: string) =>
    void linking.run(async () => {
      const link = await device.linkPatient(records.practice.id, text);
      const linked = fhir.withAccountId(record.resource as unknown as fhir.Patient, link.patient.accountId);
      await records.save({ id: patient.id, patientId: patient.id, kind: 'patient', resource: resourceOf(linked), baseVersion: record.version });
      onSaved();
    });

  if (patient.accountId) {
    return (
      <section className="card tinted" aria-label="Curentia account">
        <p data-testid="account-linked">
          <strong>Linked to {patient.name}’s Curentia account.</strong>
        </p>
        <p className="muted">
          {records.roles.includes('doctor')
            ? 'Their prescriptions and readings go to their phone, and you can ask them for their history from other practices.'
            : 'Their prescriptions and readings go to their phone, and a doctor here can ask them for their history.'}
        </p>
      </section>
    );
  }
  if (!canWrite(records.roles, 'demographics')) return null;
  return (
    <section className="card" aria-labelledby="account-heading">
      <h2 id="account-heading">Link their Curentia account</h2>
      <p className="muted">Linked patients get their prescriptions and readings on their phone, and can approve requests for their history.</p>
      <ScanButton onScan={linkWith} />
      <form
        onSubmit={(event) => {
          event.preventDefault();
          linkWith(code);
        }}
        noValidate
      >
        <Field label="Patient’s code" hint="From Show my code to a clinic, in the patient’s app" autoComplete="off" autoCapitalize="characters" spellCheck={false} value={code} onChange={(e) => setCode(e.target.value)} />
        <ErrorText error={linking.error} />
        <Submit busy={linking.busy} busyText="Linking…">
          Link with the code
        </Submit>
      </form>
      <h3>Not here in person?</h3>
      {sent ? (
        <p className="note" role="status">
          Request sent. If this number has a Curentia patient account, the patient sees the request in their app. Once they approve, they appear under Linked accounts to add on the patient list.
        </p>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void asking.run(async () => {
              await device.api.requestLink(records.practice.id, identifier);
              setSent(true);
            });
          }}
          noValidate
        >
          <Field label="Mobile number or ABHA" autoComplete="off" value={identifier} onChange={(e) => setIdentifier(e.target.value)} />
          <ErrorText error={asking.error} />
          <Submit busy={asking.busy} busyText="Sending…">
            Send a link request
          </Submit>
        </form>
      )}
    </section>
  );
}

/** Requests this practice made for the patient's history, and the form to make one. */
function HistoryCard({
  practiceId,
  patient,
  consents,
  error,
  onAsked,
}: {
  practiceId: string;
  patient: PatientView;
  consents: PublicConsent[] | null;
  error: string | null;
  onAsked: () => void;
}) {
  const mine = (consents ?? []).filter((c) => c.requesterPracticeId === practiceId);
  return (
    <section className="card" aria-labelledby="history-heading">
      <h2 id="history-heading">History from elsewhere</h2>
      <p className="muted">Ask {patient.name} to let other practices they are linked with send you their records. They decide on their phone.</p>
      <ErrorText error={error} />
      {mine.length > 0 ? (
        <ul className="list" aria-label="Requests for history">
          {mine.map((consent) => (
            <li key={consent.id} className="member">
              <span className={`badge ${consent.status}`}>{CONSENT_STATUS_LABELS[consent.status]}</span>
              <strong>{consent.hiTypes.map((type) => HI_TYPE_LABELS[type]).join(', ')}</strong>
              <span className="muted">
                Dated {formatDate(consent.from)} to {formatDate(consent.to)}. Asked by {consent.requestedByName} on {formatDate(consent.createdAt)}.
              </span>
              {consent.status === 'approved' ? (
                <span className="muted">
                  {sourcesOf(consent).length > 0 ? `Sources: ${sourcesOf(consent).join(', ')}.` : 'No source named.'} You may see them until {formatDate(consent.expiresAt)}.
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      <AskHistory practiceId={practiceId} accountId={patient.accountId ?? ''} onAsked={onAsked} />
    </section>
  );
}

function AskHistory({ practiceId, accountId, onAsked }: { practiceId: string; accountId: string; onAsked: () => void }) {
  const device = useDevice();
  const today = new Date().toISOString().slice(0, 10);
  const yearAgo = new Date(Date.now() - 365 * DAY_MS).toISOString().slice(0, 10);
  const [types, setTypes] = useState<HiType[]>(['WellnessRecord', 'Prescription']);
  const [from, setFrom] = useState(yearAgo);
  const [to, setTo] = useState(today);
  const [days, setDays] = useState<number>(30);
  const [open, setOpen] = useState(false);
  const { busy, error, setError, run } = useAction();
  const daysId = useId();
  const toggle = (type: HiType) => setTypes((now) => (now.includes(type) ? now.filter((t) => t !== type) : [...now, type]));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (types.length === 0) return setError('Choose at least one kind of record.');
    if (!from || !to || from > to) return setError('Choose dates where the start comes before the end.');
    if (to > today) return setError('The dates cannot end after today.');
    void run(async () => {
      const end = Math.min(new Date(`${to}T23:59:59.999`).getTime(), Date.now() + 12 * 60 * 60 * 1000);
      await device.api.requestConsent({
        patientId: accountId,
        practiceId,
        hiTypes: ASKABLE_TYPES.filter((t) => types.includes(t)),
        from: new Date(`${from}T00:00:00`).toISOString(),
        to: new Date(end).toISOString(),
        expiresAt: new Date(Date.now() + days * DAY_MS).toISOString(),
      });
      setOpen(false);
      onAsked();
    });
  };

  if (!open) {
    return (
      <button type="button" className="button" onClick={() => setOpen(true)}>
        Ask for their history
      </button>
    );
  }
  return (
    <form onSubmit={submit} noValidate className="ask-history">
      <h3>Ask for their history</h3>
      <fieldset>
        <legend>Records</legend>
        <div className="choices compact">
          {ASKABLE_TYPES.map((type) => (
            <label key={type} className="check">
              <input type="checkbox" checked={types.includes(type)} onChange={() => toggle(type)} />
              <span>{HI_TYPE_LABELS[type]}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <div className="pair">
        <Field label="Dated from" type="date" value={from} max={today} onChange={(e) => setFrom(e.target.value)} />
        <Field label="Up to" type="date" value={to} max={today} onChange={(e) => setTo(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor={daysId}>You may see them for</label>
        <select id={daysId} value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {ACCESS_DAYS.map((d) => (
            <option key={d} value={d}>
              {d === 365 ? 'A year' : `${d} days`}
            </option>
          ))}
        </select>
      </div>
      <p className="muted">For their care only. The patient sees exactly this and decides on their phone.</p>
      <ErrorText error={error} />
      <Submit busy={busy} busyText="Asking…">
        Ask the patient
      </Submit>
    </form>
  );
}
